import React, { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useInboxFeed from "../hooks/useInboxFeed";
import { useSiteAlerts } from "./alerts/SiteAlertsContext";
import { familyOf, tintOf } from "./alerts/alertDisplay";
import { TagIcon } from "../utils/tagIcons";
import { timeAgo } from "../utils/relativeTime";

/**
 * Unified inbox dropdown in the top bar. Two real feeds:
 *   - Sitewide alerts (Alert Center) — pinned at the top while live/targeted
 *     at this user; they leave the list when dismissed or ended.
 *   - Per-user notifications from the `notification` table (today: order-note
 *     @mentions; future system events INSERT there and appear automatically).
 *
 * The bell badge counts unread notifications + unread alerts. Clicking an
 * alert row marks it read (it keeps showing on screen — read is the inbox
 * seen-state, dismiss is the on-screen hide and lives on the banner/card).
 */

// Dispute notices: Shopmonkey reminders (services/disputes/reminders.js) and
// the Poseidon Plus autopilot's (services/disputeAutopilot/runner.js).
const DISPUTE_REMINDERS = new Set(["dispute_needs_evidence", "dispute_evidence_overdue", "dispute_not_filed"]);
const DISPUTE_AUTOPILOT = new Set(["dispute_autopilot_attention", "dispute_autopilot_deadline"]);

// ── Inline SVG icons by notification type ──
function ItemIcon({ type }) {
  if (type === "mention" || type === "message") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
    );
  }
  if (type === "customer-action") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }
  if (type === "automation" || DISPUTE_AUTOPILOT.has(type)) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (type === "due-date" || DISPUTE_REMINDERS.has(type)) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  if (type === "cs-ticket") {
    // Life buoy — customer service ticket assignment
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" />
        <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
        <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
        <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
        <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
      </svg>
    );
  }
  // system / fallback
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

const TYPE_LABEL = {
  mention: "Mention",
  message: "Message",
  "customer-action": "Customer",
  automation: "Automation",
  "due-date": "Due date",
  "cs-ticket": "CS Ticket",
  "marketing-task": "Task",
  "marketing-mention": "Mention",
  "marketing-event": "Event",
  "marketing-low-stock": "Low stock",
  "field-route": "Route",
  "field-visit-flag": "Visit flag",
  "field-lead": "Lead",
  "supply-request": "Supplies",
  "parts-order": "Parts order",
  "card-on-file": "Card added",
  dispute_needs_evidence: "Dispute",
  dispute_evidence_overdue: "Dispute",
  dispute_not_filed: "Dispute",
  dispute_autopilot_attention: "Dispute autopilot",
  dispute_autopilot_deadline: "Dispute autopilot",
};

// Icon-wrap color class per type — unknown types fall back to the message tint.
const ICON_CLASS = {
  mention: "inbox-item-icon-message",
  message: "inbox-item-icon-message",
  "customer-action": "inbox-item-icon-customer-action",
  automation: "inbox-item-icon-automation",
  "due-date": "inbox-item-icon-due-date",
  "cs-ticket": "inbox-item-icon-cs-ticket",
  "marketing-task": "inbox-item-icon-automation",
  "marketing-mention": "inbox-item-icon-message",
  "marketing-event": "inbox-item-icon-due-date",
  "marketing-low-stock": "inbox-item-icon-due-date",
  "field-route": "inbox-item-icon-automation",
  "field-visit-flag": "inbox-item-icon-customer-action",
  "field-lead": "inbox-item-icon-customer-action",
  "supply-request": "inbox-item-icon-automation",
  "parts-order": "inbox-item-icon-automation",
  "card-on-file": "inbox-item-icon-customer-action",
  dispute_needs_evidence: "inbox-item-icon-due-date",
  dispute_evidence_overdue: "inbox-item-icon-due-date",
  dispute_not_filed: "inbox-item-icon-due-date",
  dispute_autopilot_attention: "inbox-item-icon-automation",
  dispute_autopilot_deadline: "inbox-item-icon-automation",
};
const iconClassFor = (type) => ICON_CLASS[type] || "inbox-item-icon-message";

// Stored links predate the /app prefix (e.g. "/orders/123?tab=notes") —
// normalize so both old and new rows navigate correctly.
const normalizeLink = (link) => {
  if (!link || !link.startsWith("/")) return null;
  return link.startsWith("/app/") ? link : `/app${link}`;
};

/**
 * One notification row. Interaction model:
 *   - Row click = the primary action: navigate when the row has a link,
 *     expand in place when it doesn't. Marks read either way.
 *   - Chevron (bottom-right) appears only when the text is actually clamped —
 *     click expands/collapses in place WITHOUT navigating.
 *   - The dot (top-right) is a read toggle: filled = unread, click marks read
 *     without visiting; on read rows a hollow dot appears on hover to flip it
 *     back to unread. Neither control triggers the row action.
 * A div[role=button], not a <button> — the row hosts nested buttons.
 */
function InboxNotificationItem({ item, expanded, onOpen, onToggleExpand, onToggleRead }) {
  const titleRef = useRef(null);
  const summaryRef = useRef(null);
  const [clamped, setClamped] = useState(false);

  // Detect real truncation (title is 1-line ellipsis, summary 2-line clamp).
  // Skip while expanded — nothing is clamped then, but the chevron must stay
  // so the row can be collapsed again.
  useLayoutEffect(() => {
    if (expanded) return;
    const t = titleRef.current;
    const s = summaryRef.current;
    setClamped(
      Boolean(
        (t && t.scrollWidth > t.clientWidth + 1) ||
          (s && s.scrollHeight > s.clientHeight + 1)
      )
    );
  }, [expanded, item.title, item.body]);

  const link = normalizeLink(item.link);

  return (
    <div
      className={`inbox-dropdown-item ${item.read ? "" : "unread"} ${expanded ? "expanded" : ""}`}
      data-type={item.type}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item, link)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item, link);
        }
      }}
      title={link ? "Open" : expanded ? "Show less" : "Show full message"}
    >
      <span className={`inbox-item-icon-wrap ${iconClassFor(item.type)}`}>
        <ItemIcon type={item.type} />
      </span>
      <span className="inbox-item-body">
        <span className="inbox-item-top">
          <span className="inbox-item-title" ref={titleRef}>
            {item.title}
          </span>
          <span className="inbox-item-time">{timeAgo(item.created_date)}</span>
        </span>
        <span className="inbox-item-summary" ref={summaryRef}>
          {item.body}
        </span>
        <span className={`inbox-item-type-tag ${iconClassFor(item.type)}`}>
          {TYPE_LABEL[item.type] || "Update"}
        </span>
      </span>

      <button
        type="button"
        className={`inbox-item-read-toggle ${item.read ? "is-read" : "is-unread"}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleRead(item);
        }}
        title={item.read ? "Mark as unread" : "Mark as read"}
        aria-label={item.read ? "Mark as unread" : "Mark as read"}
      />
      {(clamped || expanded) && (
        <button
          type="button"
          className={`inbox-item-expand-btn ${expanded ? "open" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(item.id);
          }}
          title={expanded ? "Show less" : "Show more"}
          aria-label={expanded ? "Collapse notification" : "Expand notification"}
          aria-expanded={expanded}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function InboxMenu({ open, onToggle, tab, onTabChange, inboxRef }) {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount: notifUnread,
    markRead,
    markUnread,
    markAllRead,
  } = useInboxFeed();
  const {
    alerts,
    unreadCount: alertUnread,
    markRead: markAlertRead,
    markAllRead: markAllAlertsRead,
  } = useSiteAlerts();

  const unreadCount = notifUnread + alertUnread;
  const visibleItems =
    tab === "unread" ? notifications.filter((n) => !n.read) : notifications;
  const visibleAlerts = tab === "unread" ? alerts.filter((a) => !a.read) : alerts;

  // Long text is clamped (title 1 line, summary 2). Alerts expand on row
  // click; notification rows expand via their chevron (see
  // InboxNotificationItem for the full interaction model).
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const toggleExpanded = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAlertClick = (alert) => {
    if (!alert.read) markAlertRead(alert.id);
    toggleExpanded(alert.id);
  };

  // Row click — primary action: navigate when linked, expand otherwise.
  const handleItemOpen = (item, link) => {
    if (!item.read) markRead(item.id);
    if (link) {
      onToggle(); // close the dropdown before navigating
      navigate(link);
    } else {
      toggleExpanded(item.id);
    }
  };

  // Dot click — flip read state in place, no navigation.
  const handleToggleRead = (item) =>
    item.read ? markUnread(item.id) : markRead(item.id);

  const handleMarkAllRead = () => {
    if (notifUnread > 0) markAllRead();
    if (alertUnread > 0) markAllAlertsRead();
  };

  return (
    <div className="inbox-menu" ref={inboxRef}>
      <button
        className={`icon-button ${unreadCount > 0 ? "has-unread" : ""}`}
        onClick={onToggle}
        title="Inbox"
        aria-label="Open inbox"
      >
        <span
          className="top-bar-icon"
          style={{ "--icon-url": "url(/images/Icons/SVG/Notifications_Icon.svg)" }}
        />
        {unreadCount > 0 && (
          <span className="inbox-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="inbox-dropdown">
          <div className="inbox-dropdown-header">
            <h3 className="inbox-dropdown-title">Inbox</h3>
            <div className="inbox-dropdown-tabs">
              <button
                className={`inbox-dropdown-tab ${tab === "all" ? "active" : ""}`}
                onClick={() => onTabChange("all")}
              >
                All
              </button>
              <button
                className={`inbox-dropdown-tab ${tab === "unread" ? "active" : ""}`}
                onClick={() => onTabChange("unread")}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="inbox-dropdown-tab-badge">{unreadCount}</span>
                )}
              </button>
            </div>
          </div>

          <div className="inbox-dropdown-list">
            {visibleAlerts.length > 0 && (
              <>
                <div className="inbox-alerts-label">Active alerts</div>
                {visibleAlerts.map((alert) => {
                  const family = familyOf(alert);
                  const expanded = expandedIds.has(alert.id);
                  return (
                    <button
                      key={alert.id}
                      className={`inbox-dropdown-item inbox-alert-item ${alert.read ? "" : "unread"} ${expanded ? "expanded" : ""}`}
                      onClick={() => handleAlertClick(alert)}
                      title={expanded ? "Show less" : "Show full message"}
                      aria-expanded={expanded}
                    >
                      <span
                        className="inbox-item-icon-wrap"
                        style={{ color: family.vivid }}
                      >
                        <TagIcon name={family.icon} size={16} strokeWidth={2} />
                      </span>
                      <span className="inbox-item-body">
                        <span className="inbox-item-top">
                          <span className="inbox-item-title">{alert.message}</span>
                          <span className="inbox-item-time">{timeAgo(alert.published_at)}</span>
                        </span>
                        <span
                          className="inbox-item-type-tag"
                          style={{ color: family.vivid, background: tintOf(family.vivid) }}
                        >
                          Alert
                        </span>
                      </span>
                      {!alert.read && <span className="inbox-item-unread-dot" aria-hidden />}
                    </button>
                  );
                })}
              </>
            )}

            {visibleItems.length === 0 && visibleAlerts.length === 0 ? (
              <div className="inbox-dropdown-empty">
                <span className="inbox-dropdown-empty-icon" aria-hidden>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                  </svg>
                </span>
                <div className="inbox-dropdown-empty-title">All caught up</div>
                <div className="inbox-dropdown-empty-sub">
                  Nothing new to review right now.
                </div>
              </div>
            ) : (
              visibleItems.map((item) => (
                <InboxNotificationItem
                  key={item.id}
                  item={item}
                  expanded={expandedIds.has(item.id)}
                  onOpen={handleItemOpen}
                  onToggleExpand={toggleExpanded}
                  onToggleRead={handleToggleRead}
                />
              ))
            )}
          </div>

          <div className="inbox-dropdown-footer">
            <button
              className="inbox-dropdown-action"
              disabled={unreadCount === 0}
              onClick={handleMarkAllRead}
            >
              Mark all as read
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
